// lib
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  ButtonGroup,
  FormControl,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
} from "@material-ui/core";
import {
  DateRange,
  NavigateBefore,
  NavigateNext,
  Refresh,
  ViewDay,
  ViewWeek,
} from "@material-ui/icons";
// icons
import { getTrackedRange } from "utils/ranges";
import TimelineIcon from "./icons/Timeline";
import MonthIcon from "./icons/Month";
import FiveDaysIcon from "./icons/FiveDaysAlt";
// src

const linkAsEmail = (link) => link.includes("@") && !link.startsWith("http");

const MENU_ICONS = {
  day: ViewDay,
  threeDays: ViewWeek,
  fiveDays: FiveDaysIcon,
  week: DateRange,
  month: MonthIcon,
  timeline: TimelineIcon,
};

const formatTitle = (title, titleLink) => {
  if (titleLink) {
    if (linkAsEmail(titleLink)) {
      return <a href={`mailto:${titleLink}`}>{title}</a>;
    }
    return (
      <a href={titleLink} target="_blank" rel="noopener noreferrer">
        {title}
      </a>
    );
  }
  return <div>{title}</div>;
};

const Toolbar = ({
  // from parent
  localizer: { messages },
  label,
  onNavigate,
  onView,
  view,
  views,
  // data props
  focus,
  isLarge,
  selfTranslate,
  title,
  titleLink,
  // action props
  refresh,
  updateRangeAndFetch,
}) => {
  const titleEl = formatTitle(selfTranslate(title), titleLink);

  //viewsCleaned, without threeDays
  const viewsCleaned = views.filter((v) => v !== "threeDays");
  const [currentView, setCurrentView] = useState(view);
  React.useEffect(() => {
    setCurrentView(view);
  }, [view]);

  const handleChange = ({ target: { value: newView } }) => {
    // dispatch newView before conditionnally triggering updateRangeAndFetch
    onView(newView);
    // in both cases Calendar handleRange won't be triggered
    if (view === "timeline" || newView === "timeline") {
      const [dateStart, dateEnd] = getTrackedRange(focus, newView);
      updateRangeAndFetch(dateStart, dateEnd);
    }
    // last: toolbar display and update view inner state variable
    setCurrentView(newView);
  };

  return (
    <div className="rbc-toolbar">
      <ButtonGroup size="small" aria-label="navigation du calendrier">
        {isLarge && (
          <IconButton onClick={refresh}>
            <Refresh />
          </IconButton>
        )}
        <Button className="today" onClick={() => onNavigate("TODAY")}>
          {messages.today}
        </Button>
        <IconButton color="primary" onClick={() => onNavigate("PREV")} aria-label="précédent">
          <NavigateBefore />
        </IconButton>
        <IconButton color="primary" onClick={() => onNavigate("NEXT")} aria-label="suivant">
          <NavigateNext />
        </IconButton>
      </ButtonGroup>

      <div className="cal-toolbar-title">
        {titleEl}
        <div>{label}</div>
      </div>

      <div className="cal-toolbar-view">
        <FormControl size="small" variant="outlined">
          <Select value={currentView} onChange={handleChange}>
            {viewsCleaned.map((name) => {
              const Icon = MENU_ICONS[name];
              return (
                <MenuItem key={name} value={name}>
                  <ListItemIcon className="app-select-icon">
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={messages[name]} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};

Toolbar.propTypes = {
  // from parent
  view: PropTypes.string.isRequired,
  views: PropTypes.arrayOf(PropTypes.string).isRequired,
  label: PropTypes.node.isRequired,
  localizer: PropTypes.object.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  // data props
  focus: PropTypes.object.isRequired,
  isLarge: PropTypes.bool.isRequired,
  selfTranslate: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  titleLink: PropTypes.string,
  // ations props
  refresh: PropTypes.func.isRequired,
  updateRangeAndFetch: PropTypes.func.isRequired,
};

Toolbar.defaultProps = {
  title: "",
  titleLink: "",
};

export default Toolbar;
